// src/pages/UtilityAllowanceViewerPage.js
// Public viewer for one authority's utility allowance schedule.
// Renders the exact same UtilityAllowance component the signed-in dashboard
// uses (same API, same DB row, same PDF), just without the analytics/ACOP
// chrome around it. Header mirrors TranslateViewerPage's StickyHeader:
// frosted sticky bar, back pill on the left, centered brand | org | badge.
// Route: /utility-allowances/:org

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Brand from '../components/Brand';
import UtilityAllowance from './UtilityAllowance';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const T = {
  blue600: '#2563eb',
  blue100: '#dbeafe',
  blue50: '#eff6ff',
  gray900: '#111827',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radiusPill: '999px',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// Orgs whose viewer page is reachable by direct URL. Being here does NOT
// list an org on the hub (that's the hub's own list, where `pending` keeps
// cards unclickable) — this only gates /utility-allowances/:org itself.
const HOUSING_AUTHORITIES = {
  salem: { name: 'Salem', state: 'MA' },
  concord: { name: 'Concord', state: 'MA' },
  waltham: { name: 'Waltham', state: 'MA' },
  winchester: { name: 'Winchester', state: 'MA' },
  arlington: { name: 'Arlington', state: 'MA' },
  andover: { name: 'Andover', state: 'MA' },
  somerville: { name: 'Somerville', state: 'MA', ext: 'jpg' },
  reading: { name: 'Reading', state: 'MA' },
  'north-reading': { name: 'North Reading', state: 'MA', ext: 'jpg' },
  watertown: { name: 'Watertown', state: 'MA' },
  lexington: { name: 'Lexington', state: 'MA' },
  methuen: { name: 'Methuen', state: 'MA', ext: 'jpg' },
  melrose: { name: 'Melrose', state: 'MA' },
  beverly: { name: 'Beverly', state: 'MA', ext: 'jpg' },
  'new-bedford': { name: 'New Bedford', state: 'MA' },
  brockton: { name: 'Brockton', state: 'MA' },
  hudson: { name: 'Hudson', state: 'MA' },
  haverhill: { name: 'Haverhill', state: 'MA' },
  stockbridge: { name: 'Stockbridge', state: 'MA' },
  tewksbury: { name: 'Tewksbury', state: 'MA' },
  revere: { name: 'Revere', state: 'MA' },
  framingham: { name: 'Framingham', state: 'MA' },
  holliston: { name: 'Holliston', state: 'MA' },
  quincy: { name: 'Quincy', state: 'MA' },
  hingham: { name: 'Hingham', state: 'MA' },
  holbrook: { name: 'Holbrook', state: 'MA' },
  hanson: { name: 'Hanson', state: 'MA' },
  wakefield: { name: 'Wakefield', state: 'MA' },
  brookline: { name: 'Brookline', state: 'MA' },
  danvers: { name: 'Danvers', state: 'MA', ext: 'webp' },
  woburn: { name: 'Woburn', state: 'MA' },
  'fall-river': { name: 'Fall River', state: 'MA' },
  // Reachable by URL for delivery; hub card stays `pending` until adoption.
  greenfield: { name: 'Greenfield', state: 'MA', ext: 'jpg' },
  pittsfield: { name: 'Pittsfield', state: 'MA', ext: 'webp' },
  // Not a "Housing Authority": the city's Community Development Authority
  // is the PHA (MA070) and runs Section 8 through its housing division.
  marlborough: { name: 'Marlborough', state: 'MA', fullName: 'Marlborough Community Development Authority' },
  // Regional administering agency (non-MTW vouchers): leased-unit-weighted
  // composite of the eight towns it leases in. Reachable by URL; the hub card
  // stays `pending` until the agency says to list it.
  neighborworks: { name: 'NeighborWorks', state: 'MA', fullName: 'NeighborWorks Housing Solutions' },
};

export default function UtilityAllowanceViewerPage({ sample = false }) {
  const navigate = useNavigate();
  const { org } = useParams();
  const mobile = useIsMobile();
  const slug = (org || '').toLowerCase();
  const ha = HOUSING_AUTHORITIES[slug];

  const backButton = (
    <button
      onClick={() => navigate('/utility-allowances')}
      style={{
        background: T.gray100,
        border: `1px solid ${T.gray200}`,
        borderRadius: T.radiusPill,
        cursor: 'pointer',
        fontSize: '0.85rem',
        color: T.gray600,
        padding: '0.45rem 0.85rem',
        transition: 'all 0.15s ease',
        fontFamily: T.font,
        fontWeight: 600,
      }}
      title="All Housing Authorities"
      onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.borderColor = T.blue600; e.currentTarget.style.color = T.blue600; }}
      onMouseOut={e => { e.currentTarget.style.background = T.gray100; e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.color = T.gray600; }}
    >
      ←
    </button>
  );

  const orgLogo = (size) => (
    <img
      src={`/${slug}-logo.${ha?.ext || 'png'}`}
      alt=""
      style={{ height: `${size}px`, width: `${size}px`, objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }}
    />
  );

  const badge = (
    <span style={{
      fontSize: mobile ? '0.72rem' : '0.85rem',
      padding: mobile ? '0.2rem 0.55rem' : '0.25rem 0.75rem',
      background: T.blue100,
      color: T.blue600,
      borderRadius: T.radiusPill,
      fontWeight: 600,
      fontFamily: T.font,
    }}>
      Utility Allowance Schedule
    </span>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ fontFamily: T.font }}>
      {/* Sticky frosted header, same structure as the translate viewer */}
      <header style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.gray200}`,
        padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: mobile ? '0.5rem' : '0.75rem',
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          {/* Row 1: Back button + branding */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            justifyContent: mobile ? 'flex-start' : 'center',
            position: 'relative',
          }}>
            {!mobile && <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>{backButton}</div>}
            {mobile && backButton}
            <Brand as="a" href="https://gethousingai.com" target="_blank" rel="noopener noreferrer" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', fontFamily: T.font, textDecoration: 'none' }} />
            {!mobile && ha && (
              <>
                <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
                <span style={{
                  fontWeight: 700,
                  color: T.gray900,
                  fontSize: '1rem',
                  fontFamily: T.font,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}>
                  {orgLogo(28)} {ha.fullName || `${ha.name} Housing Authority`}
                </span>
                {badge}
              </>
            )}
          </div>

          {/* Row 2 (mobile only): Org name + badge */}
          {mobile && ha && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}>
              <span style={{
                fontWeight: 700,
                color: T.gray900,
                fontSize: '0.85rem',
                fontFamily: T.font,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
              }}>
                {orgLogo(22)}
                {ha.fullName || `${ha.name} Housing Authority`}
              </span>
              {badge}
            </div>
          )}
        </div>
      </header>

      {/* Schedule, same component the dashboard renders */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {ha ? (
            <>
              <UtilityAllowance currentUser={slug} forceSample={sample} />
              {/* Join CTA — worded for THIS context: the reader is viewing
                  another authority's schedule (e.g. a portability lookup) */}
              <div className="mt-8 p-4 sm:p-5 bg-blue-50 border border-dashed border-blue-600 rounded-xl text-center">
                <span className="text-sm text-gray-800">Want a schedule like this for your housing authority? </span>
                <button
                  onClick={() => navigate('/utility-allowances/join')}
                  className="text-sm font-bold text-blue-600 underline hover:text-blue-800"
                >
                  Get your schedule →
                </button>
              </div>
            </>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Unknown housing authority</h3>
              <p className="text-gray-500 mb-4">No published utility allowance schedule for &ldquo;{slug}&rdquo;.</p>
              <button
                onClick={() => navigate('/utility-allowances')}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                See available authorities
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-between gap-3">
          <Brand theme="dark" markClassName="text-blue-600" className="font-semibold text-sm" />
          <div className="text-sm text-gray-400">© 2026 HousingAI. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
